// import { AlertTrigger, GlobalLoading } from '@/components';
import {
	IgnoreMessageCodeInInterceptor,
	MessageCodeToLogout,
	ServerMessageCode,
} from '@/constant';
import { AuthActions, store } from '@/stores';
import {
	getDeviceToken,
	getErrorMessageWithTranslation,
	showAlert,
} from '@/utils';
import { isMobile } from '@/utils/mobile.util';

import axios, {
	AxiosError,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';

import { ApiResponseError } from './api.type';

export * from './api.type';
export * from './auth.api';
export * from './common.api';
export * from './define-document-area';
export * from './device.api';
export * from './foreign.api';
export * from './my-page.api';
export * from './system.api';
export * from './worker.api';

const instance = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
	withCredentials: false,
});

// Real client IP forwarding.
// Browsers cannot read their own public IP, so resolve it once from an echo
// service and cache it. It is then sent on every request via the
// X-Forwarded-For header so the BE HttpUtils.getClientIP can read the real
// client IP. Best-effort: if resolution fails, BE falls back to remoteAddr.
const CLIENT_IP_ENDPOINT =
	process.env.REACT_APP_CLIENT_IP_URL || 'https://api.ipify.org?format=json';

let cachedClientIp: string | null = null;

const primeClientIp = (): void => {
	fetch(CLIENT_IP_ENDPOINT)
		.then((res) => res.json())
		.then((data) => {
			cachedClientIp = data?.ip ?? null;
		})
		.catch(() => {
			// swallow: header is optional, BE has its own fallback
			cachedClientIp = null;
		});
};

if (typeof window !== 'undefined') {
	primeClientIp();
}

// Track detail API calls to set Is-Audit header
// First call: Is-Audit = true, subsequent calls: Is-Audit = false
const detailApiCallTracker = new Set<string>();

// Reset tracker function - can be called when popup closes or page refreshes
export const resetDetailApiTracker = (): void => {
	detailApiCallTracker.clear();
};

// Reset tracker when page unloads (F5, close tab, etc.)
if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', () => {
		resetDetailApiTracker();
	});

	// Reset tracker when page becomes visible again (after being hidden)
	// This handles cases where user switches tabs and comes back
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			// Optional: reset when page becomes visible again
			// Uncomment if needed: resetDetailApiTracker();
		}
	});
}

const AUDIT_MONITORING_PATH = ['/detail', 'worker/publicSalary/list'];

const IgnoreCheckDomainURLInterceptor = [
	'/oauth/url',
	'/oauth/gpkiUrl',
	'/device/client/mobile/register',
];

instance.interceptors.request.use(
	(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
		const accessToken = store.getState()?.auth?.accessToken;

		// Device-client endpoints authenticate via X-Device-Token instead of
		// (or in addition to) the admin Bearer token. Spec: TAB-02/TAB-03 +
		// /device/client/check all require X-Device-Token once registered.
		const url = config.url ?? '';
		if (url.includes('/device/client/')) {
			const deviceToken = getDeviceToken();
			if (deviceToken) {
				config.headers['X-Device-Token'] = deviceToken;
			}
		}

		// Forward the resolved real client IP to BE (HttpUtils.getClientIP)
		// via X-Forwarded-For. If not resolved yet, retry priming for
		// subsequent requests.
		if (cachedClientIp) {
			config.headers['X-Forwarded-For'] = cachedClientIp;
		} else {
			primeClientIp();
		}

		if (accessToken) {
			config.headers['Authorization'] = 'Bearer ' + accessToken;
			config.headers['Is-Mobile'] = isMobile ? 1 : 0;

			const params = config.params || {};

			if (params?.screenCodeForAudit) {
				config.headers['Screen-Code'] = params?.screenCodeForAudit;
				delete config.params?.screenCodeForAudit;
			}

			// Check if this is a detail API call
			const url = config.url || '';
			if (
				AUDIT_MONITORING_PATH.some((path) => url.includes(path)) ||
				(typeof params?.isAudit !== 'undefined' && params?.isAudit)
			) {
				// Create unique key for this detail API call
				const apiKey = `${config.method?.toUpperCase() || 'GET'}:${url}`;

				if (!detailApiCallTracker.has(apiKey)) {
					// First call to this detail API
					config.headers['Is-Audit'] = 1;
					detailApiCallTracker.add(apiKey);
				} else {
					// Subsequent calls to the same detail API
					config.headers['Is-Audit'] = 0;
				}
			}
			if (typeof params?.isAudit !== 'undefined' && !params?.isAudit) {
				config.headers['Is-Audit'] = 0;
				delete config.params?.isAudit;
			}
		}

		return config;
	},
	(error: AxiosError) => {
		// GlobalLoading.hide();
		return Promise.reject(error);
	}
);

instance.interceptors.response.use(
	(response: AxiosResponse): AxiosResponse => {
		// GlobalLoading.hide();
		return response;
	},
	async (err: ApiResponseError) => {
		// GlobalLoading.hide();
		if (axios.isAxiosError(err)) {
			// if cancel by AbortController => ignore
			if (err.code === ServerMessageCode.ERR_CANCELED) {
				return Promise.reject(err);
			}

			if (err?.code === ServerMessageCode.ERR_NETWORK) {
				showAlert(getErrorMessageWithTranslation(err?.code));
				return;
			}

			const errorKey: ServerMessageCode = err?.response?.data?.messageCode;
			const message = err?.response?.data?.message;

			// Device-session terminated by BE (admin revoke or token invalidated).
			// Skip the alert and logout silently — AuthRouter then calls
			// /device/client/check and redirects to the appropriate screen
			// (MOB-01 for REVOKED, register flow for invalid token).
			if (
				err.response?.status === 403 &&
				errorKey === ServerMessageCode.DM_DEVICE_ACCESS_REVOKED
			) {
				store.dispatch(AuthActions.logoutRequest());
				return Promise.reject(err);
			}

			if (!IgnoreMessageCodeInInterceptor.includes(errorKey)) {
				showAlert(getErrorMessageWithTranslation(errorKey), () => {
					if (MessageCodeToLogout.includes(errorKey)) {
						store.dispatch(AuthActions.logoutRequest());
					}
				});
			}

			const url = err?.response?.config?.url ?? '';

			const status = err.response?.status;

			if (
				!IgnoreMessageCodeInInterceptor.includes(errorKey) &&
				!IgnoreCheckDomainURLInterceptor.includes(url)
			) {
				showAlert(getErrorMessageWithTranslation(errorKey), () => {
					if (
						[
							ServerMessageCode.SYS_DOMAIN_INVALID,
							ServerMessageCode.SYS_DOMAIN_PERMISSION_DENIED,
							ServerMessageCode.SYS_DOMAIN_INACTIVE,
						].includes(errorKey) ||
						status === 401
					) {
						store.dispatch(AuthActions.logoutRequest());
						return;
					}
				});
				return;
			}

			switch (status) {
				case 401: {
					store.dispatch(AuthActions.logoutRequest());
					break;
				}
				default: {
					console.error('error message', message);
					break;
				}
			}
		}

		return Promise.reject(err);
	}
);

export default instance;
